import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {Modal} from '@shared/Modal';
import Input from '@shared/Input';
import Button from '@shared/Button';
import ToggleSwitch from '@shared/ToggleSwitch';
import { Eye, EyeOff, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '@/utils/api';

// Zod schema for user validation
const userSchema = z.object({
  name: z.string().min(1, 'Full Name is required'),
  email: z.email('Invalid email format').min(1, 'Email is required'),
  password: z.string().min(8, 'Password must be at least 8 characters').optional().or(z.literal('')), // Optional for edit mode
  confirmPassword: z.string().optional().or(z.literal('')),
  role: z.enum(['admin', 'user', 'viewer'], { message: 'Invalid role selected' }),
  status: z.enum(['active', 'inactive'], { message: 'Invalid status selected' }),
  department: z.string().optional(),
  permissions: z.array(z.string()).optional(),
}).refine((data) => {
  if (data.password && data.password !== data.confirmPassword) {
    return false;
  }
  return true;
}, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type UserFormData = z.infer<typeof userSchema>;

interface User {
  _id: string;
  name?: string;
  email?: string;
  role?: 'admin' | 'user' | 'viewer';
  status?: 'active' | 'inactive';
  department?: string;
  permissions?: string[];
}

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  user?: User | null;
}

const UserFormModal: React.FC<UserFormModalProps> = ({ isOpen, onClose, onSave, user }) => {
  const isEditMode = !!user;
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, watch, setValue } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'user',
      status: 'active',
      department: '',
      permissions: [],
    }
  });

  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0); // 0: weak, 1: medium, 2: strong

  useEffect(() => {
    if (isOpen) {
      if (user) {
        reset({
          name: user.name || '',
          email: user.email || '',
          role: user.role || 'user',
          status: user.status || 'active',
          department: user.department || '',
          permissions: user.permissions || [],
          password: '', // Passwords are not pre-filled for security
          confirmPassword: '',
        });
      } else {
        reset();
      }
      setPasswordStrength(0);
    }
  }, [isOpen, user, reset]);

  const currentPassword = watch('password');
  useEffect(() => {
    if (currentPassword) {
      // Simple password strength check
      let strength = 0;
      if (currentPassword.length >= 8) strength++;
      if (/[A-Z]/.test(currentPassword)) strength++;
      if (/[0-9]/.test(currentPassword)) strength++;
      if (/[^A-Za-z0-9]/.test(currentPassword)) strength++;
      setPasswordStrength(Math.min(strength, 3));
    } else {
      setPasswordStrength(0);
    }
  }, [currentPassword]);

  const generateStrongPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()';
    let newPassword = '';
    for (let i = 0; i < 12; i++) {
      newPassword += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setValue('password', newPassword, { shouldValidate: true });
    setValue('confirmPassword', newPassword, { shouldValidate: true });
    toast.success('Strong password generated!');
  };

  const onSubmit = async (data: UserFormData) => {
    try {
      if (isEditMode) {
        // Remove password fields if not changed or empty for update
        const updates = { ...data };
        if (!updates.password) {
          delete updates.password;
          delete updates.confirmPassword;
        }
        await api.updateUser(user!._id, updates);
        toast.success('User updated successfully!');
      } else {
        await api.createUser(data);
        toast.success('User created successfully!');
      }
      onSave();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save user.');
      console.error('Error saving user:', error);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength === 0) return 'text-gray-400';
    if (passwordStrength === 1) return 'text-red-500';
    if (passwordStrength === 2) return 'text-orange-500';
    return 'text-green-500';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength === 0) return 'No password';
    if (passwordStrength === 1) return 'Weak';
    if (passwordStrength === 2) return 'Medium';
    return 'Strong';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditMode ? 'Edit User' : 'Add New User'}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name</label>
          <Input
            id="name"
            type="text"
            {...register('name')}
            className="mt-1 block w-full"
            error={!!errors.name?.message}
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
          <Input
            id="email"
            type="email"
            {...register('email')}
            className="mt-1 block w-full"
            error={!!errors.email?.message}
            disabled={isEditMode} // Email usually not editable
          />
        </div>

        {!isEditMode && (
          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  {...register('password')}
                  className="block w-full pr-10"
                  error={!!errors.password?.message}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <div className="flex justify-between items-center mt-1 text-sm">
                <span className={getPasswordStrengthColor()}>
                  Strength: {getPasswordStrengthText()}
                </span>
                <Button type="button" variant="ghost" size="sm" onClick={generateStrongPassword}>
                  <RefreshCw className="h-4 w-4 mr-1" /> Generate Strong Password
                </Button>
              </div>
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">Confirm Password</label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                {...register('confirmPassword')}
                className="mt-1 block w-full pr-10"
                error={!!errors.confirmPassword?.message}
              />
            </div>
          </div>
        )}

        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-700">Role</label>
          <select
            id="role"
            {...register('role')}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
            <option value="viewer">Viewer</option>
          </select>
          {errors.role && <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>}
        </div>

        <div>
          <label htmlFor="department" className="block text-sm font-medium text-gray-700">Department (Optional)</label>
          <Input
            id="department"
            type="text"
            {...register('department')}
            className="mt-1 block w-full"
            error={!!errors.department?.message}
          />
        </div>

        {isEditMode && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <ToggleSwitch
              id="status"
              label={watch('status') === 'active' ? 'Active' : 'Inactive'}
              checked={watch('status') === 'active'}
              onChange={(checked) => setValue('status', checked ? 'active' : 'inactive')}
            />
            {errors.status && <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>}
          </div>
        )}

        {/* Permissions section - simplified for now, can be expanded */}
        {/* <div>
          <label className="block text-sm font-medium text-gray-700">Permissions</label>
          <div className="mt-2 space-y-2">
            <label className="flex items-center">
              <input type="checkbox" {...register('permissions')} value="view_documents" className="form-checkbox" />
              <span className="ml-2 text-gray-700">View Documents</span>
            </label>
            <label className="flex items-center">
              <input type="checkbox" {...register('permissions')} value="upload_documents" className="form-checkbox" />
              <span className="ml-2 text-gray-700">Upload Documents</span>
            </label>
          </div>
        </div> */}

        <div className="flex justify-end space-x-3 mt-6">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            {isEditMode ? 'Update User' : 'Add User'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UserFormModal;
